import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Hourglass } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

// Hoisted motion configs — referenced by stable identity at every render so
// framer-motion doesn't think they changed between ticks.
const COUNTDOWN_INITIAL = { y: 14, opacity: 0, scale: 0.85 };
const COUNTDOWN_ANIMATE = { y: 0, opacity: 1, scale: 1 };
const COUNTDOWN_EXIT = { y: -14, opacity: 0, scale: 0.85 };
const COUNTDOWN_TRANSITION = { type: "spring", stiffness: 280, damping: 22 };

function computeRemaining(target) {
  const diff = target.getTime() - Date.now();
  if (diff <= 0) {
    return { expired: true, days: 0, hours: 0, minutes: 0, seconds: 0 };
  }
  return {
    expired: false,
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    minutes: Math.floor((diff % 3600000) / 60000),
    seconds: Math.floor((diff % 60000) / 1000),
  };
}

const UNIT_STYLES = [
  { color: "#D81B8C", bg: "from-[#FCE7F3] to-white", ring: "ring-[#D81B8C]/15" },
  { color: "#3FA535", bg: "from-[#E8F6E5] to-white", ring: "ring-[#3FA535]/15" },
  { color: "#6B2C8F", bg: "from-[#EFE3F5] to-white", ring: "ring-[#6B2C8F]/15" },
  { color: "#D81B8C", bg: "from-[#FCE7F3] to-white", ring: "ring-[#D81B8C]/15" },
];

export default function EventCountdown({ targetDate }) {
  const { t } = useLanguage();
  const [time, setTime] = useState(() => computeRemaining(targetDate));

  useEffect(() => {
    setTime(computeRemaining(targetDate));
    const id = setInterval(() => setTime(computeRemaining(targetDate)), 1000);
    return () => clearInterval(id);
  }, [targetDate]);

  if (time.expired) {
    return (
      <div
        data-testid="countdown-expired"
        className="mt-6 rounded-2xl py-4 px-5 text-center text-white font-display font-black text-lg sm:text-xl tracking-tight"
        style={{
          background:
            "linear-gradient(95deg, #D81B8C 0%, #6B2C8F 50%, #3FA535 100%)",
        }}
      >
        {t("event.eventStarted")}
      </div>
    );
  }

  const units = [
    { value: time.days, label: t("event.days") },
    { value: time.hours, label: t("event.hours") },
    { value: time.minutes, label: t("event.minutes") },
    { value: time.seconds, label: t("event.seconds") },
  ];

  return (
    <div className="mt-6" data-testid="countdown">
      <div className="flex items-center gap-2 mb-3">
        <Hourglass className="w-3.5 h-3.5 text-[#D81B8C]" />
        <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#D81B8C]">
          {t("event.countdown")}
        </p>
      </div>

      <div className="grid grid-cols-4 gap-2 sm:gap-3">
        {units.map((u, i) => {
          const style = UNIT_STYLES[i];
          return (
            <div
              key={u.label}
              data-testid={`countdown-${u.label.toLowerCase()}`}
              className={`relative rounded-2xl bg-gradient-to-b ${style.bg} ring-1 ${style.ring} py-3 sm:py-4 px-1 text-center overflow-hidden`}
            >
              <div className="relative h-9 sm:h-11 flex items-center justify-center">
                <AnimatePresence mode="popLayout" initial={false}>
                  <motion.span
                    key={u.value}
                    initial={COUNTDOWN_INITIAL}
                    animate={COUNTDOWN_ANIMATE}
                    exit={COUNTDOWN_EXIT}
                    transition={COUNTDOWN_TRANSITION}
                    className="absolute font-display text-2xl sm:text-3xl font-black tabular-nums"
                    style={{ color: style.color }}
                  >
                    {String(u.value).padStart(2, "0")}
                  </motion.span>
                </AnimatePresence>
              </div>
              <div className="mt-1 text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-[#5C4664]">
                {u.label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
